import { Controller } from '@hotwired/stimulus'

/**
 * Контроллер формы выбора локали.
 *
 * Связывает выбор страны и языка: при выборе страны оставляет доступными
 * только языки, перечисленные в `data-languages` выбранной опции страны.
 * При отправке формы передаёт серверу фактический адрес открытой страницы,
 * чтобы после смены языка пользователь остался в том же разделе.
 *
 * @example Опция страны
 * <option value="kz" data-languages="ru,kk">Kazakhstan</option>
 */
export default class LocalesController extends Controller {
  static targets = [
    'countrySelect',
    'languageSelect',
    'countrySelectContainer',
    'languageSelectContainer',
    'returnTo'
  ]

  /**
   * Инициализирует доступные языки для уже выбранной страны.
   *
   * Ожидает один кадр анимации, чтобы кастомный select успел создать свой
   * выпадающий список и его можно было синхронизировать с disabled-состояниями.
   */
  async initialize() {
    if (this.hasCountrySelectTarget) {
      await new Promise((resolve) => {
        requestAnimationFrame(resolve)
      })

      const selectedCountryOption = this.countrySelectTarget.selectedOptions[0]
      if (
        selectedCountryOption &&
        !selectedCountryOption.hasAttribute('data-placeholder')
      ) {
        const languages =
          selectedCountryOption.dataset.languages?.split(',') || []
        this.#setLanguages(languages)
      }
    }
  }

  /**
   * Обработчик изменения страны.
   *
   * Читает список доступных языков из `data-languages` выбранной опции и
   * обновляет языковой select.
   *
   * @param {Event} event — событие `change` от select страны
   */
  countryChanged(event) {
    const selectedOption = event.target.selectedOptions[0]

    const languages = selectedOption.dataset.languages?.split(',') || []
    this.#setLanguages(languages)
  }

  /**
   * Обработчик отправки формы локали.
   *
   * Сохраняет актуальный путь страницы перед отправкой формы.
   *
   * @param {SubmitEvent} event — событие отправки формы
   */
  submit() {
    this.returnToTarget.value = `${window.location.pathname}${window.location.search}${window.location.hash}`
  }

  /**
   * Ограничивает список языков заданным набором значений.
   *
   * Синхронизирует disabled-состояние нативных `<option>` и визуальных опций
   * кастомного select, затем сбрасывает текущее значение языка.
   *
   * @param {string[]} languages — список разрешённых кодов языков
   */
  #setLanguages(languages) {
    const languagesSet = new Set(languages)
    const selectId = this.languageSelectContainerTarget.dataset.selectId
    const languageDropdown = document.getElementById(`${selectId}-listbox`)

    languageDropdown?.querySelectorAll('.select__option').forEach((option) => {
      option.classList.toggle(
        'is-disabled',
        !languagesSet.has(option.dataset.value)
      )
    })

    this.languageSelectTarget
      .querySelectorAll('option:not([data-placeholder])')
      .forEach((option) => {
        option.disabled = !languagesSet.has(option.value)
      })

    this.#languageSelectController?.setValue(null)
  }

  /**
   * Возвращает контроллер кастомного select для поля языка.
   *
   * @returns {import('@hotwired/stimulus').Controller | null}
   */
  get #languageSelectController() {
    return this.application.getControllerForElementAndIdentifier(
      this.languageSelectContainerTarget,
      'select'
    )
  }
}
